import * as React from "react";

import {useApolloGqlClient, type ApolloGqlClient} from "@khan/data-gql-apollo";

export type WithApolloProps = {
    gqlClient: ApolloGqlClient;
};

export type WithoutApollo<TProps> = Omit<TProps, keyof WithApolloProps>;

/**
 * HOC to inject the current Gql routing info into a component.
 */
const withApollo = <
    C extends React.ComponentType<React.ComponentProps<C> & WithApolloProps> & {
        displayName?: string;
    },
>(
    WrappedComponent: C,
) => {
    type OuterProps = Omit<
        JSX.LibraryManagedAttributes<C, React.ComponentProps<C>>,
        keyof WithApolloProps
    >;

    // Try to create a nice displayName for React Dev Tools.
    const displayName =
        WrappedComponent.displayName || WrappedComponent.name || "Component";

    // Creating the inner component. The calculated Props type here is the where the magic happens.
    const ComponentWithApollo = (props: OuterProps) => {
        const {gqlClient} = useApolloGqlClient();

        return <WrappedComponent {...(props as any)} gqlClient={gqlClient} />;
    };

    ComponentWithApollo.displayName = `withApollo(${displayName})`;

    return ComponentWithApollo;
};

export default withApollo;
